import { apiClient } from "../../../../lib/axios";

export interface CreateClientRequest {
  name: string;
  is_active: boolean;
  enable_anonymisation?: boolean;
  learning_locker_enabled?: boolean;
  occupational_health_enabled?: boolean;
  wearables_enabled?: boolean;
  akea_life_enabled?: boolean;
}

export interface EnabledServices {
  learning_locker: boolean;
  occupational_health: boolean;
  wearables: boolean;
  akea_life: boolean;
}

export interface ServiceOverrides {
  learning_locker: boolean | null;
  occupational_health: boolean | null;
  wearables: boolean | null;
  akea_life: boolean | null;
}

export interface CreateClientResponse {
  data: {
    client: {
      id: string;
      name: string;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
      createdBy?: string | null;
      updatedBy?: string | null;
    };
    created_departments: number;
    created_job_roles: number;
  };
}

export interface CreateDepartmentRequest {
  name: string;
  is_active: boolean;
}

export interface CreateJobRoleRequest {
  name: string;
  is_active: boolean;
}

export interface ClientDepartment {
  id: string;
  name: string;
  is_active?: boolean;
}

export interface ClientJobRole {
  id: string;
  name: string;
  is_active?: boolean;
}

export interface ClientUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  departments: ClientDepartment[];
  job_roles: ClientJobRole[];
  enabled_services?: EnabledServices;
  service_overrides?: ServiceOverrides;
  integrations?: {
    provider: string;
    name: string;
    icon?: string | null;
    connected: boolean;
  }[];
}

export interface ClientUsersResponse {
  data: ClientUser[];
}

export interface ClientUserCountResponse {
  data: {
    count: number;
  };
}

export interface ClientJobRolesResponse {
  data: ClientJobRole[];
}

export interface ClientSummary {
  id: string;
  name: string;
  is_active?: boolean;
  isActive?: boolean;
  enable_anonymisation?: boolean;
  enableAnonymisation?: boolean;
  learning_locker_enabled?: boolean;
  learningLockerEnabled?: boolean;
  occupational_health_enabled?: boolean;
  occupationalHealthEnabled?: boolean;
  wearables_enabled?: boolean;
  wearablesEnabled?: boolean;
  akea_life_enabled?: boolean;
  akeaLifeEnabled?: boolean;
}

export interface ClientDetails extends ClientSummary {
  qrCodeValue?: string | null;
  qrCodeUrl?: string | null;
}

export interface ClientDetailsResponse {
  data: ClientDetails | null;
}

export interface ClientsResponse {
  data: ClientSummary[];
}

export interface CentralUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  client?: ClientSummary | null;
  departments: ClientDepartment[];
  job_roles: ClientJobRole[];
  enabled_services?: EnabledServices;
  service_overrides?: ServiceOverrides;
  integrations?: {
    provider: string;
    name: string;
    icon?: string | null;
    connected: boolean;
  }[];
}

export interface CentralUsersResponse {
  data: CentralUser[];
}

export interface CentralDepartmentsResponse {
  data: ClientDepartment[];
}

export interface CentralJobRolesResponse {
  data: ClientJobRole[];
}

export interface ClientDashboardSetting {
  dashboard_key: string;
  is_enabled: boolean;
  ai_enabled?: boolean | null;
}

export interface ClientDashboardSettingsResponse {
  data: ClientDashboardSetting[];
}

export interface DefaultDepartmentsResponse {
  data: ClientDepartment[];
}

export interface DefaultJobRolesResponse {
  data: ClientJobRole[];
}

export const clientManagementApi = {
  createClient: async (
    payload: CreateClientRequest
  ): Promise<CreateClientResponse> => {
    const response = await apiClient.post("/admin/clients", payload);
    return response.data;
  },

  listClients: async (): Promise<ClientsResponse> => {
    const response = await apiClient.get("/admin/clients");
    return response.data;
  },

  getClientDetails: async (
    clientId: string
  ): Promise<ClientDetailsResponse> => {
    const response = await apiClient.get(`/admin/clients/${clientId}`);
    return response.data;
  },

  updateClient: async (
    clientId: string,
    payload: Partial<CreateClientRequest>
  ) => {
    const response = await apiClient.put(`/admin/clients/${clientId}`, payload);
    return response.data;
  },

  createDepartment: async (clientId: string, payload: CreateDepartmentRequest) => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/departments`,
      payload
    );
    return response.data;
  },

  updateDepartment: async (
    departmentId: string,
    payload: Partial<CreateDepartmentRequest>
  ) => {
    const response = await apiClient.put(
      `/admin/departments/${departmentId}`,
      payload
    );
    return response.data;
  },

  removeUserFromDepartment: async (departmentId: string, userId: string) => {
    const response = await apiClient.delete(
      `/admin/departments/${departmentId}/users/${userId}`
    );
    return response.data;
  },

  deleteDepartment: async (departmentId: string) => {
    const response = await apiClient.delete(`/admin/departments/${departmentId}`);
    return response.data;
  },

  createJobRole: async (clientId: string, payload: CreateJobRoleRequest) => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/job-roles`,
      payload
    );
    return response.data;
  },

  listClientUsers: async (clientId: string): Promise<ClientUsersResponse> => {
    const response = await apiClient.get(`/admin/clients/${clientId}/users`);
    return response.data;
  },

  getClientUserCount: async (
    clientId: string
  ): Promise<ClientUserCountResponse> => {
    const response = await apiClient.get(
      `/admin/clients/${clientId}/user-count`
    );
    return response.data;
  },

  listJobRoles: async (clientId: string): Promise<ClientJobRolesResponse> => {
    const response = await apiClient.get(
      `/admin/clients/${clientId}/job-roles`
    );
    return response.data;
  },

  updateJobRole: async (
    jobRoleId: string,
    payload: Partial<CreateJobRoleRequest>
  ) => {
    const response = await apiClient.put(
      `/admin/job-roles/${jobRoleId}`,
      payload
    );
    return response.data;
  },

  removeUserFromJobRole: async (jobRoleId: string, userId: string) => {
    const response = await apiClient.delete(
      `/admin/job-roles/${jobRoleId}/users/${userId}`
    );
    return response.data;
  },

  deleteJobRole: async (jobRoleId: string) => {
    const response = await apiClient.delete(`/admin/job-roles/${jobRoleId}`);
    return response.data;
  },

  listCentralUsers: async (
    clientId?: string | null
  ): Promise<CentralUsersResponse> => {
    const response = await apiClient.get("/admin/central/users", {
      params: clientId ? { client_id: clientId } : undefined,
    });
    return response.data;
  },

  listCentralDepartments: async (
    clientId?: string | null
  ): Promise<CentralDepartmentsResponse> => {
    const response = await apiClient.get("/admin/central/departments", {
      params: clientId ? { client_id: clientId } : undefined,
    });
    return response.data;
  },

  listCentralJobRoles: async (
    clientId?: string | null
  ): Promise<CentralJobRolesResponse> => {
    const response = await apiClient.get("/admin/central/job-roles", {
      params: clientId ? { client_id: clientId } : undefined,
    });
    return response.data;
  },

  listClientDashboardSettings: async (
    clientId: string
  ): Promise<ClientDashboardSettingsResponse> => {
    const response = await apiClient.get(
      `/admin/clients/${clientId}/dashboard-settings`
    );
    return response.data;
  },

  updateClientDashboardSetting: async (
    clientId: string,
    dashboardKey: string,
    payload: { is_enabled?: boolean; ai_enabled?: boolean | null }
  ) => {
    const response = await apiClient.put(
      `/admin/clients/${clientId}/dashboard-settings/${dashboardKey}`,
      payload
    );
    return response.data;
  },

  listDefaultDepartments: async (): Promise<DefaultDepartmentsResponse> => {
    const response = await apiClient.get("/admin/default/departments");
    return response.data;
  },

  createDefaultDepartment: async (payload: CreateDepartmentRequest) => {
    const response = await apiClient.post("/admin/default/departments", payload);
    return response.data;
  },

  updateDefaultDepartment: async (
    departmentId: string,
    payload: Partial<CreateDepartmentRequest>
  ) => {
    const response = await apiClient.put(
      `/admin/default/departments/${departmentId}`,
      payload
    );
    return response.data;
  },

  deleteDefaultDepartment: async (departmentId: string) => {
    const response = await apiClient.delete(
      `/admin/default/departments/${departmentId}`
    );
    return response.data;
  },

  listDefaultJobRoles: async (): Promise<DefaultJobRolesResponse> => {
    const response = await apiClient.get("/admin/default/job-roles");
    return response.data;
  },

  createDefaultJobRole: async (payload: CreateJobRoleRequest) => {
    const response = await apiClient.post("/admin/default/job-roles", payload);
    return response.data;
  },

  updateDefaultJobRole: async (
    jobRoleId: string,
    payload: Partial<CreateJobRoleRequest>
  ) => {
    const response = await apiClient.put(
      `/admin/default/job-roles/${jobRoleId}`,
      payload
    );
    return response.data;
  },

  deleteDefaultJobRole: async (jobRoleId: string) => {
    const response = await apiClient.delete(
      `/admin/default/job-roles/${jobRoleId}`
    );
    return response.data;
  },

  assignUserToClient: async (clientId: string, userId: string) => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/users/${userId}`
    );
    return response.data;
  },

  removeUserFromClient: async (userId: string) => {
    const response = await apiClient.delete(`/admin/clients/users/${userId}`);
    return response.data;
  },

  assignUserToDepartment: async (departmentId: string, userId: string) => {
    const response = await apiClient.post(
      `/admin/departments/${departmentId}/users/${userId}`
    );
    return response.data;
  },

  assignUserToJobRole: async (jobRoleId: string, userId: string) => {
    const response = await apiClient.post(
      `/admin/job-roles/${jobRoleId}/users/${userId}`
    );
    return response.data;
  },

  updateUserServices: async (
    userId: string,
    payload: Partial<ServiceOverrides>
  ) => {
    const response = await apiClient.put(`/admin/users/${userId}/services`, payload);
    return response.data;
  },
};
